(ns arthur.flow.ingest
  "Read footage from the server: source timing, one video to measure, and one
  content-addressed URL per tracing still.

  THE MEASURED PIXELS COME OUT OF A VIDEO NOW, not out of a PNG per frame. The old
  arrangement stored 112MB for a 7.6-second take and 1.1GB at the 900-frame limit;
  the same footage is a 6MB H.264 proxy. What that cost was the property a PNG
  sequence gave for free — that asking for frame 12 gets frame 12 — and `decode!`
  below is how it is bought back."
  (:require [arthur.fx.http :as http]
            [clojure.string :as str]))

(defn feature-presence
  "Expand one-based, inclusive absence intervals from a manifest into boolean
  observation tracks. Unknown features are rejected by freeze, where the scene
  knows its feature IDs."
  [frames absence]
  (when (some? absence)
    (when-not (map? absence)
      (throw (ex-info "feature-absence must be a map of feature IDs to intervals"
                      {:feature-absence absence})))
    (into {}
          (map (fn [[id intervals]]
                 (when-not (and (keyword? id) (sequential? intervals)
                                (every? (fn [span]
                                          (and (vector? span) (= 2 (count span))
                                               (every? integer? span)
                                               (<= 1 (first span) (second span) frames)))
                                        intervals))
                   (throw (ex-info "feature-absence intervals must be [first last] source frames"
                                   {:feature id :intervals intervals :frames frames})))
                 [id (mapv (fn [frame]
                             (not-any? (fn [[first-frame last-frame]]
                                         (<= first-frame frame last-frame))
                                       intervals))
                           (range 1 (inc frames)))]))
          absence)))

(defn- valid-manifest [m]
  (let [fps (js/Number (:fps m))
        frames (js/Number (:frames m))
        urls (:urls m)]
    (when-not (and (js/Number.isFinite fps) (pos? fps)
                   (string? (:audio m)) (seq (:audio m))
                   (sequential? urls) (every? string? urls))
      (throw (ex-info "a footage manifest needs fps, frames (1–900), audio and a url per frame"
                      {:manifest (dissoc m :urls)})))
    ;; Named as its own failure rather than folded into the check above, because
    ;; it has a specific cause and a specific fix: this footage was extracted
    ;; before the proxy existed, and its frames were stored as PNGs that the
    ;; measurement path no longer reads.
    (when-not (and (string? (:stream m)) (seq (:stream m)))
      (throw (ex-info "this footage has no decodable stream — re-extract it from its source"
                      {:footage (:id m)})))
    (when-not (= frames (count urls))
      ;; The count is the manifest's and the URLs are the manifest's, so a
      ;; disagreement between them is the server contradicting itself — and it
      ;; would present as a take that is silently short.
      (throw (ex-info "the manifest's frame count and its list of frames disagree"
                      {:frames frames :urls (count urls)})))
    (assoc m :fps fps :frames frames :urls (vec urls)
           :presence (feature-presence frames (:feature-absence m)))))

(defn available!
  "Every extracted take the server holds."
  []
  (-> (http/GET "/api/footage")
      (.then (fn [json] (:footage (js->clj json :keywordize-keys true))))))

(defn manifest!
  "One take's manifest, including a URL per frame."
  [id]
  (-> (http/GET (str "/api/footage/" id))
      (.then (fn [json] (valid-manifest (js->clj json :keywordize-keys true))))))

(defn detector!
  "Who is about to do the detecting, as the server understands it: the MediaPipe
  package version and the hash of the model asset it serves.

  ASKED RATHER THAN ASSUMED, because this string ends up inside every block's
  content address, and a version constant in the client is one somebody has to
  remember to bump. The server serves the model, so it can hash it — and then the
  version is a fact about the bytes that produced the landmarks."
  []
  (-> (http/GET "/api/detector")
      (.then (fn [json] (js->clj json :keywordize-keys true)))))

(defn slice
  "The manifest of source frames `[start end)` of this footage, as though that
  were all of it: its frame count, its tracing stills and its absence tracks cut
  to the range, and `:range` saying where it came from — which goes into the
  analysis address, see `flow/address`. The whole footage is returned unchanged,
  so a full-length conversion reuses analyses made before ranges existed."
  [m start end]
  (if (= [start end] [0 (:frames m)])
    m
    (-> m
        (assoc :frames (- end start) :range [start end])
        (update :urls #(some-> % vec (subvec start end)))
        (update :presence #(into {} (map (fn [[id track]] [id (subvec (vec track) start end)])) %)))))

(defn audio-url [manifest]
  (:audio manifest))

(defn video-url [manifest]
  (:video manifest))

(defn stream-url [manifest]
  (:stream manifest))

(defn frame-url
  "The tracing still for one frame. A reference image for drawing over — the
  landmarks and the mouth crops come from the video, not from these."
  [manifest i]
  (nth (:urls manifest) i))

(defn image! [src]
  (js/Promise.
   (fn [resolve reject]
     (let [image (js/Image.)]
       (set! (.-onload image) #(resolve image))
       (set! (.-onerror image) #(reject (ex-info (str "frame did not load: " src)
                                                {:src src})))
       (set! (.-src image) src)))))

;; ---------------------------------------------------------------------------
;; decoding the proxy
;;
;; WEBCODECS, NOT SEEKING, and the seeking is worth a paragraph because three
;; separate failures came out of it. A `<video>` cannot be asked for frame 12: it
;; can be asked for a TIME, and which frame that lands on is up to the engine.
;; Measured on a video whose every frame carries its own index in its pixels,
;; Firefox 156 returned the wrong frame for 4 of 40 seeks aimed at the middle of
;; each frame, and 15 of 40 aimed at the start — and `requestVideoFrameCallback`,
;; the thing that is supposed to say which frame arrived, reported a different
;; frame from the one actually on screen 27 times out of 40. There is no way to
;; verify a seek when the verification is the part that is wrong.
;;
;; So the page decodes instead. `VideoDecoder` takes coded chunks and returns
;; exactly one frame per chunk, in order — measured 120 of 120 in order in both
;; Firefox and Chrome. The server hands us the proxy's video as a raw Annex-B
;; stream, which needs no demuxer: NAL start codes are findable in a loop. And
;; because the proxy is encoded with no B-frames, decode order is presentation
;; order, so ACCESS UNIT k IS FRAME k. No timestamps are interpreted, no clock is
;; reconciled, and nothing here can be off by one.

(defn frame-ms
  "When source frame `i` happens, in milliseconds of footage.

  What `flow/detect` hands MediaPipe as the frame's timestamp. Real elapsed time
  rather than the frame number, because the tracker reads the gap between
  timestamps as motion — see `detect/detect!` — and strictly increasing, which its
  input stream requires."
  [fps i]
  (/ (* i 1000) fps))

(defn access-units
  "An Annex-B H.264 stream -> one `{:from :to :key?}` per coded frame.

  A NAL unit starts at a three- or four-byte start code, and an access unit is the
  parameter sets and SEI leading up to and including one coded slice. So: a new
  unit begins at the first non-slice NAL after a slice. Types 1 and 5 are the
  slice types — 5 is an IDR, which is what makes a chunk a keyframe.

  Twenty-five lines instead of a demuxer, and the reason it is only twenty-five is
  that the stream was produced for this: constant rate, no B-frames, one slice per
  frame."
  [^js bytes]
  (let [n (.-length bytes)
        starts (loop [i 0 out (transient [])]
                 (if (>= i (- n 3))
                   (persistent! out)
                   (let [a (aget bytes i) b (aget bytes (inc i)) c (aget bytes (+ i 2))]
                     (cond
                       (and (zero? a) (zero? b) (= 1 c))
                       (recur (inc i) (conj! out i))

                       (and (zero? a) (zero? b) (zero? c) (= 1 (aget bytes (+ i 3))))
                       (recur (+ i 2) (conj! out i))

                       :else (recur (inc i) out)))))]
    (loop [ks 0 current nil saw-slice? false out []]
      (if (= ks (count starts))
        (if current (conj out current) out)
        (let [start (nth starts ks)
              end (if (< (inc ks) (count starts)) (nth starts (inc ks)) n)
              header (aget bytes (+ start (if (= 1 (aget bytes (+ start 2))) 3 4)))
              kind (bit-and header 0x1f)
              slice? (or (= 1 kind) (= 5 kind))
              [out current saw-slice?] (if (and slice? saw-slice?)
                                         [(conj out current) nil false]
                                         [out current saw-slice?])
              current (or current {:from start :to end :key? false})]
          (recur (inc ks)
                 (assoc current :to end :key? (or (:key? current) (= 5 kind)))
                 (or saw-slice? slice?)
                 out))))))

(defn stream!
  "Fetch the elementary stream and cut it into one chunk per frame."
  [src frames]
  (-> (js/fetch src)
      (.then (fn [^js response]
               (when-not (.-ok response)
                 (throw (ex-info (str "the footage's video stream did not load: "
                                      (.-status response))
                                 {:src src})))
               (.arrayBuffer response)))
      (.then (fn [buffer]
               (let [units (access-units (js/Uint8Array. buffer))]
                 (when-not (= (count units) frames)
                   (throw (ex-info (str "the video stream holds " (count units)
                                        " coded frames and the manifest says " frames)
                                   {:units (count units) :frames frames})))
                 {:bytes (js/Uint8Array. buffer) :units (vec units)})))))

(def ^:private decode-lookahead
  "How many chunks may be in the decoder at once.

  Backpressure, and not a tuning knob to leave at infinity: a 900-frame take at
  1440x1920 is gigabytes of decoded frames, so feeding the whole stream in and
  letting the output callback keep up is how the tab dies. Small enough to bound
  that, and more than one so the decoder is never idle waiting on us."
  4)

(defn decode!
  "Decode every frame in order, calling `(on-frame i frame)` for each.

  `on-frame` runs while the frame is alive and must not retain it — this closes it
  as soon as the call returns, because a `VideoFrame` holds decoder memory and the
  decoder stalls when it runs out. It may return a promise, which the walk waits
  for before feeding more; that is what lets a synchronous MediaPipe call and a
  repaint happen between frames without the decoder running ahead.

  Resolves when the last frame has been handed over."
  [{:keys [bytes units]} fps width height on-frame]
  (js/Promise.
   (fn [resolve reject]
     (if-not (exists? js/VideoDecoder)
       (reject (ex-info (str "this browser has no WebCodecs VideoDecoder, which is "
                             "what reads footage a frame at a time")
                        {}))
       (let [total (count units)
             next-in (volatile! 0)
             done-out (volatile! 0)
             failed (volatile! false)
             ;; `taken` is claimed as a frame ARRIVES. `done-out` counts frames
             ;; FINISHED, and is what backpressure and completion read. They are
             ;; two different numbers whenever `on-frame` yields, which it does —
             ;; reading `done-out` as the index let frames 0 and 1 both claim 0,
             ;; call the detector twice at timestamp 0, and get the run killed by
             ;; `Packet timestamp mismatch ... expected 1 but received 0`.
             taken (volatile! 0)
             ;; And the work is CHAINED rather than started, because the detector
             ;; is a tracker fed one frame at a time in order. Two overlapping
             ;; `detect!` calls are not slow, they are wrong.
             chain (volatile! (js/Promise.resolve))
             decoder (volatile! nil)
             fail! (fn [error]
                     (when-not @failed
                       (vreset! failed true)
                       (when-let [^js d @decoder]
                         (try (when-not (= "closed" (.-state d)) (.close d))
                              (catch :default _ nil)))
                       (reject error)))]
         (letfn [(feed! []
                   (while (and (not @failed)
                               (< @next-in total)
                               (< (- @next-in @done-out) decode-lookahead))
                     (let [i @next-in
                           {:keys [from to key?]} (nth units i)]
                       (vreset! next-in (inc i))
                       (.decode ^js @decoder
                                (js/EncodedVideoChunk.
                                 #js {:type (if key? "key" "delta")
                                      :timestamp (js/Math.round (/ (* i 1e6) fps))
                                      :duration (js/Math.round (/ 1e6 fps))
                                      :data (.subarray bytes from to)})))))]
           (vreset!
            decoder
            (js/VideoDecoder.
             #js {:output
                  (fn [^js frame]
                    (if @failed
                      (.close frame)
                      (let [i @taken]
                        (vreset! taken (inc i))
                        (vreset!
                         chain
                         (.then
                          @chain
                          (fn []
                            (if @failed
                              (do (try (.close frame) (catch :default _ nil)) nil)
                              (-> (js/Promise.resolve
                                   (try (on-frame i frame)
                                        (catch :default error (js/Promise.reject error))))
                                  (.then (fn [_]
                                           (try (.close frame) (catch :default _ nil))
                                           (vreset! done-out (inc i))
                                           (if (= (inc i) total)
                                             (do (.close ^js @decoder)
                                                 (resolve total))
                                             (feed!))))
                                  (.catch (fn [error]
                                            (try (.close frame) (catch :default _ nil))
                                            (fail! error)
                                            nil))))))))))
                  :error (fn [^js e]
                           (fail! (ex-info (str "the browser could not decode this "
                                                "footage: " (.-message e))
                                           {})))}))
           (.configure ^js @decoder
                       #js {:codec "avc1.640028"
                            :codedWidth width :codedHeight height
                            :optimizeForLatency true})
           (feed!)))))))
